/**
 * Global image-load concurrency limiter.
 *
 * Why: Home / Multimedia pages render many remote S3 images at once. Letting
 * them all fetch simultaneously opens a burst of parallel TLS connections that
 * S3 / an intermediary / the WebView socket pool can reset mid-flight
 * (`net::ERR_CONNECTION_RESET`), leaving images blank.
 *
 * This caps how many image loads run at once. Consumers (ImageLoader) acquire a
 * slot before assigning the <img> src and release it on load/error, so the
 * actual <img> performs a SINGLE request (no separate preload) while staying
 * throttled. A tiny promise-based semaphore — no external dependency.
 */

// Browsers already cap ~6 connections per host; staying at/under that keeps the
// un-CDN'd S3 bucket from seeing the dozens-at-once burst that triggered
// net::ERR_CONNECTION_RESET, while draining the queue fast enough that prefetch
// (below) can stay ahead of the scroll.
const MAX_CONCURRENT = 6;

let active = 0;
// Two priority lanes: visible <img> renders jump ahead of background prefetch
// so warming upcoming images never delays what's already on screen.
const renderWaiters = [];
const prefetchWaiters = [];

function pump() {
  if (active >= MAX_CONCURRENT) return;
  const grant = renderWaiters.shift() || prefetchWaiters.shift();
  if (!grant) return;
  active += 1;
  grant();
}

/**
 * Acquire a load slot. Resolves with a one-shot `release()` function.
 * Pass `prefetch: true` for low-priority cache-warming so it yields to renders.
 */
export function acquireSlot({ prefetch = false } = {}) {
  return new Promise((resolve) => {
    let released = false;
    const grant = () => {
      resolve(() => {
        if (released) return;
        released = true;
        active -= 1;
        pump();
      });
    };
    (prefetch ? prefetchWaiters : renderWaiters).push(grant);
    pump();
  });
}

// URLs whose bytes are already in the browser / service-worker cache (a real
// <img> render or a prefetch completed). Lets ImageLoader skip the lazy gate
// for already-warm images so they paint instantly with no flash.
const warmed = new Set();

export function isWarmed(url) {
  return typeof url === "string" && warmed.has(url);
}

export function markWarmed(url) {
  if (typeof url === "string" && url) warmed.add(url);
}

/**
 * Prefetch a remote image into the browser / service-worker cache ahead of
 * render. Uses a detached <img> (request.destination === 'image') so it shares
 * the same StaleWhileRevalidate cache entry the on-screen <img> will later read
 * — no duplicate S3 hit once warm. Throttled via the prefetch lane so it never
 * bursts or starves visible renders.
 */
export function prefetchImage(url) {
  if (typeof url !== "string" || !url || warmed.has(url)) {
    return Promise.resolve();
  }
  warmed.add(url); // optimistic: dedupe concurrent prefetch requests
  return acquireSlot({ prefetch: true }).then(
    (release) =>
      new Promise((resolve) => {
        const img = new Image();
        const done = () => {
          release();
          resolve();
        };
        img.onload = done;
        img.onerror = () => {
          // Let the real render retry from scratch if warming failed.
          warmed.delete(url);
          done();
        };
        img.decoding = "async";
        img.src = url;
      }),
  );
}

/** Promise-based delay used for retry backoff. */
export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
